import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'

interface Strategy {
  id: string
  user_id: string
  name: string
  version: number
  display_name: string
  description: string | null
  entry_rules: string | null
  exit_rules: string | null
  version_notes: string | null
  image_url: string | null
  is_public: boolean
  is_active: boolean
  is_locked: boolean
  created_at: string
  updated_at: string
}

interface Trade {
  id: string
  strategy_id: string | null
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  entry_date: string | null
  exit_date: string | null
  entry_price: number
  exit_price: number | null
  pnl: number | null
  pnl_percentage: number | null
  currency: string | null
  long_short: 'long' | 'short'
}

interface VersionStats {
  version: number
  strategy: Strategy
  totalTrades: number
  closedTrades: number
  openTrades: number
  winningTrades: number
  losingTrades: number
  winRate: number
  totalPnl: number
  avgPnl: number
  avgPnlPercent: number | null
  profitFactor: number | null
  trades: Trade[]
}

function StrategyDetail({ onBack, embedded }: { onBack: () => void; embedded?: boolean }) {
  const { strategyName } = useParams<{ strategyName: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const goBack = embedded ? () => navigate('/app/strategies') : onBack
  const { isPro } = useMembership()
  const { showWarning } = useNotification()
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [versionStats, setVersionStats] = useState<VersionStats[]>([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formEntryRules, setFormEntryRules] = useState('')
  const [formExitRules, setFormExitRules] = useState('')
  const [formImage, setFormImage] = useState<File | null>(null)
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null)
  const [formIsPublic, setFormIsPublic] = useState(false)
  const [formIsActive, setFormIsActive] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [overallStats, setOverallStats] = useState<{
    totalTrades: number
    closedTrades: number
    openTrades: number
    winningTrades: number
    losingTrades: number
    winRate: number
    totalPnl: number
    avgPnl: number
    avgPnlPercent: number | null
    profitFactor: number | null
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user || !strategyName) return

    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Decode strategy name from URL
        const decodedName = decodeURIComponent(strategyName)

        // Fetch all versions of this strategy
        const { data: strategiesData, error: strategiesError } = await supabase
          .from('strategies')
          .select('*')
          .eq('user_id', user.id)
          .eq('name', decodedName)
          .order('version', { ascending: true })

        if (strategiesError) throw strategiesError

        if (!strategiesData || strategiesData.length === 0) {
          setError('Strategy not found')
          setLoading(false)
          return
        }

        setStrategies(strategiesData)

        // Fetch trades for all strategy versions
        const strategyIds = strategiesData.map(s => s.id)
        const { data: tradesData, error: tradesError } = await supabase
          .from('trades_with_market_data')
          .select('id, strategy_id, symbol, market, entry_date, exit_date, entry_price, exit_price, pnl, pnl_percentage, currency, long_short')
          .in('strategy_id', strategyIds)
          .order('entry_date', { ascending: false })

        if (tradesError) throw tradesError

        const trades = (tradesData || []) as Trade[]

        // Note: We'll fetch trades per version below

        // Fetch trades per strategy version
        const statsWithTrades = await Promise.all(
          strategiesData.map(async (strategy) => {
            const { data: versionTrades, error: versionError } = await supabase
              .from('trades_with_market_data')
              .select('id, strategy_id, symbol, market, entry_date, exit_date, entry_price, exit_price, pnl, pnl_percentage, currency, long_short')
              .eq('strategy_id', strategy.id)
              .order('entry_date', { ascending: false })

            if (versionError) throw versionError

            return calculateVersionStats(strategy, (versionTrades || []) as Trade[])
          })
        )

        setVersionStats(statsWithTrades)

        // Calculate overall stats (all versions combined)
        const allTrades = trades
        setOverallStats(calculateOverallStats(allTrades))

      } catch (err: any) {
        setError(err.message || 'Failed to load strategy details')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user, strategyName])

  const calculateVersionStats = (strategy: Strategy, trades: Trade[]): VersionStats => {
    const closedTrades = trades.filter(t => t.exit_date && t.pnl !== null)
    const openTrades = trades.filter(t => !t.exit_date)
    const winningTrades = closedTrades.filter(t => t.pnl !== null && t.pnl > 0)
    const losingTrades = closedTrades.filter(t => t.pnl !== null && t.pnl < 0)

    const totalTrades = trades.length
    const winRate = closedTrades.length > 0 
      ? (winningTrades.length / closedTrades.length) * 100 
      : 0

    const totalPnl = closedTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
    const avgPnl = closedTrades.length > 0 ? totalPnl / closedTrades.length : 0

    const pnlPctTrades = closedTrades.filter(t => t.pnl_percentage !== null)
    const avgPnlPercent = pnlPctTrades.length > 0
      ? pnlPctTrades.reduce((sum, t) => sum + (t.pnl_percentage || 0), 0) / pnlPctTrades.length
      : null

    const totalProfit = winningTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
    const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl || 0), 0))
    const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : (totalProfit > 0 ? Infinity : null)

    return {
      version: strategy.version,
      strategy,
      totalTrades,
      closedTrades: closedTrades.length,
      openTrades: openTrades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      winRate,
      totalPnl,
      avgPnl,
      avgPnlPercent,
      profitFactor,
      trades
    }
  }

  const calculateOverallStats = (trades: Trade[]) => {
    const closedTrades = trades.filter(t => t.exit_date && t.pnl !== null)
    const openTrades = trades.filter(t => !t.exit_date)
    const winningTrades = closedTrades.filter(t => t.pnl !== null && t.pnl > 0)
    const losingTrades = closedTrades.filter(t => t.pnl !== null && t.pnl < 0)

    const totalTrades = trades.length
    const winRate = closedTrades.length > 0 
      ? (winningTrades.length / closedTrades.length) * 100 
      : 0

    const totalPnl = closedTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
    const avgPnl = closedTrades.length > 0 ? totalPnl / closedTrades.length : 0

    const pnlPctTrades = closedTrades.filter(t => t.pnl_percentage !== null)
    const avgPnlPercent = pnlPctTrades.length > 0
      ? pnlPctTrades.reduce((sum, t) => sum + (t.pnl_percentage || 0), 0) / pnlPctTrades.length
      : null

    const totalProfit = winningTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
    const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl || 0), 0))
    const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : (totalProfit > 0 ? Infinity : null)

    return {
      totalTrades,
      closedTrades: closedTrades.length,
      openTrades: openTrades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      winRate,
      totalPnl,
      avgPnl,
      avgPnlPercent,
      profitFactor
    }
  }

  const formatNumber = (value: number | null, currency?: string | null, digits = 2) => {
    if (value === null || Number.isNaN(value)) return '-'
    const formatted = value.toFixed(digits).replace('.', ',')
    return currency ? `${formatted} ${currency}` : formatted
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
        <div className={embedded ? "py-8" : "max-w-6xl mx-auto px-6 py-12"}>
          <p className="text-center text-gray-500">Loading strategy details...</p>
        </div>
      </div>
    )
  }

  if (error || !strategies.length) {
    return (
      <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
        <div className={embedded ? "py-4" : "max-w-6xl mx-auto px-6 py-12"}>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Strategy not found'}
          </div>
          <button
            onClick={goBack}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Back to Strategies
          </button>
        </div>
      </div>
    )
  }

  const mainStrategy = strategies[0] // First version (version 0) is the main strategy

  return (
    <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
      <header className={embedded ? "w-full py-4 mb-4" : "w-full py-6 px-6 max-w-6xl mx-auto"}>
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={goBack}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors"
            >
              <span aria-hidden="true">←</span>
              Back
            </button>
            <h1 className="text-3xl font-bold mt-2">{mainStrategy.name}</h1>
            {mainStrategy.description && (
              <p className="text-sm text-gray-500 mt-1">{mainStrategy.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {mainStrategy.is_public && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                Public
              </span>
            )}
            {mainStrategy.is_active ? (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                Active
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                Inactive
              </span>
            )}
            <button
              onClick={() => {
                if (mainStrategy) {
                  // Check if strategy is locked
                  if (mainStrategy.is_locked) {
                    const newerVersions = strategies.filter(s => s.version > mainStrategy.version)
                    showWarning(`Bu strateji versiyonu kilitlenmiştir ve düzenlenemez. Daha yeni versiyonlar mevcut (${newerVersions.length} versiyon). Lütfen en son versiyonu düzenleyin.`)
                    return
                  }

                  setEditingStrategy(mainStrategy)
                  setFormName(mainStrategy.name)
                  setFormDescription(mainStrategy.description || '')
                  setFormEntryRules(mainStrategy.entry_rules || '')
                  setFormExitRules(mainStrategy.exit_rules || '')
                  setFormIsPublic(mainStrategy.is_public)
                  setFormIsActive(mainStrategy.is_active)
                  setFormImage(null)
                  setFormImagePreview(mainStrategy.image_url)
                  setShowEditModal(true)
                }
              }}
              disabled={mainStrategy?.is_locked}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                mainStrategy?.is_locked
                  ? 'text-gray-400 cursor-not-allowed bg-gray-200'
                  : 'text-white bg-orange-500 hover:bg-orange-600'
              }`}
              title={mainStrategy?.is_locked ? 'Bu versiyon kilitlenmiştir' : 'Stratejiyi düzenle'}
            >
              Edit Strategy
            </button>
          </div>
        </div>
      </header>

      <main className={embedded ? "w-full space-y-6 pb-6" : "max-w-6xl mx-auto px-6 pb-16 space-y-8"}>
        {/* Strategy Image */}
        {mainStrategy.image_url && (
          <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <img
              src={mainStrategy.image_url}
              alt={mainStrategy.name}
              className="w-full h-64 object-cover rounded-lg"
            />
          </section>
        )}

        {/* Entry Rules and Exit Rules */}
        {(mainStrategy.entry_rules || mainStrategy.exit_rules) && (
          <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <h2 className="text-2xl font-semibold mb-4">Strategy Rules</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mainStrategy.entry_rules && (
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Entry Rules</h3>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {mainStrategy.entry_rules}
                  </div>
                </div>
              )}
              {mainStrategy.exit_rules && (
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Exit Rules</h3>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {mainStrategy.exit_rules}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Overall Statistics */}
        {overallStats && (
          <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <h2 className="text-2xl font-semibold mb-4">Overall Statistics (All Versions)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500">Total Trades</p>
                <p className="text-2xl font-bold text-gray-900">{overallStats.totalTrades}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {overallStats.closedTrades} closed, {overallStats.openTrades} open
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500">Win Rate</p>
                <p className="text-2xl font-bold text-gray-900">{overallStats.winRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {overallStats.winningTrades}W / {overallStats.losingTrades}L
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500">Total P&L</p>
                <p className={`text-2xl font-bold ${overallStats.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatNumber(overallStats.totalPnl)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Avg: {formatNumber(overallStats.avgPnl)}
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500">Profit Factor</p>
                <p className="text-2xl font-bold text-gray-900">
                  {overallStats.profitFactor === null ? '-' : overallStats.profitFactor === Infinity ? '∞' : overallStats.profitFactor.toFixed(2)}
                </p>
                {overallStats.avgPnlPercent !== null && (
                  <p className="text-xs text-gray-500 mt-1">
                    Avg P&L%: {formatNumber(overallStats.avgPnlPercent, null, 2)}%
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Version Statistics */}
        <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
          <h2 className="text-2xl font-semibold mb-4">Version Statistics</h2>
          <div className="space-y-6">
            {versionStats.map((stats) => (
              <div key={stats.strategy.id} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {stats.strategy.display_name}
                  </h3>
                  <div className="flex items-center gap-2">
                    {stats.strategy.is_locked && (
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600" title="Bu versiyon kilitlenmiştir ve düzenlenemez">
                        🔒 Locked
                      </span>
                    )}
                    {stats.strategy.image_url && (
                      <img
                        src={stats.strategy.image_url}
                        alt={stats.strategy.display_name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    {stats.strategy.is_public && (
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                        Public
                      </span>
                    )}
                  </div>
                </div>

                {stats.strategy.description && (
                  <p className="text-sm text-gray-600 mb-4">{stats.strategy.description}</p>
                )}

                {stats.strategy.version_notes && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Version Notes</h4>
                    <p className="text-sm text-blue-800 whitespace-pre-wrap">{stats.strategy.version_notes}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Total Trades</p>
                    <p className="text-lg font-bold text-gray-900">{stats.totalTrades}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {stats.closedTrades} closed, {stats.openTrades} open
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Win Rate</p>
                    <p className="text-lg font-bold text-gray-900">{stats.winRate.toFixed(1)}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {stats.winningTrades}W / {stats.losingTrades}L
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Total P&L</p>
                    <p className={`text-lg font-bold ${stats.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(stats.totalPnl)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Avg: {formatNumber(stats.avgPnl)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Profit Factor</p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.profitFactor === null ? '-' : stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Avg P&L%</p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.avgPnlPercent !== null ? `${formatNumber(stats.avgPnlPercent, null, 2)}%` : '-'}
                    </p>
                  </div>
                </div>

                {/* Recent Trades */}
                {stats.trades.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Trades</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 text-gray-600">Symbol</th>
                            <th className="text-left py-2 px-3 text-gray-600">Entry</th>
                            <th className="text-left py-2 px-3 text-gray-600">Exit</th>
                            <th className="text-right py-2 px-3 text-gray-600">P&L</th>
                            <th className="text-right py-2 px-3 text-gray-600">P&L%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.trades.slice(0, 5).map((trade) => (
                            <tr key={trade.id} className="border-b border-gray-100">
                              <td className="py-2 px-3">
                                <div className="font-medium">{trade.symbol}</div>
                                <div className="text-xs text-gray-500">{trade.market}</div>
                              </td>
                              <td className="py-2 px-3 text-gray-600">{formatDate(trade.entry_date)}</td>
                              <td className="py-2 px-3 text-gray-600">{formatDate(trade.exit_date)}</td>
                              <td className={`py-2 px-3 text-right font-medium ${trade.pnl !== null && trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatNumber(trade.pnl, trade.currency)}
                              </td>
                              <td className="py-2 px-3 text-right text-gray-600">
                                {trade.pnl_percentage !== null ? `${formatNumber(trade.pnl_percentage, null, 2)}%` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {stats.trades.length > 5 && (
                      <p className="text-xs text-gray-500 mt-2">
                        Showing 5 of {stats.trades.length} trades
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Edit Modal */}
      {showEditModal && editingStrategy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="rounded-2xl p-6 max-w-2xl w-full mx-4 my-8 shadow-xl max-h-[90vh] overflow-y-auto bg-app-box">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit Strategy</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingStrategy(null)
                  setEditError(null)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {editError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Strategy Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter strategy name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Describe your strategy..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entry Rules</label>
                <textarea
                  value={formEntryRules}
                  onChange={(e) => setFormEntryRules(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Define entry conditions and rules..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exit Rules</label>
                <textarea
                  value={formExitRules}
                  onChange={(e) => setFormExitRules(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Define exit conditions and rules..."
                />
              </div>

              {isPro && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image (Optional)</label>
                    {formImagePreview ? (
                      <div className="relative">
                        <img src={formImagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          onClick={() => {
                            setFormImage(null)
                            setFormImagePreview(null)
                          }}
                          className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              setEditError('Image size must be less than 5MB')
                              return
                            }
                            setFormImage(file)
                            const reader = new FileReader()
                            reader.onloadend = () => {
                              setFormImagePreview(reader.result as string)
                            }
                            reader.readAsDataURL(file)
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    )}
                    {editingStrategy.image_url && !formImagePreview && (
                      <p className="text-xs text-gray-500 mt-1">Current image will be kept if no new image is uploaded</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formIsPublic}
                        onChange={(e) => setFormIsPublic(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Make public</span>
                    </label>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formIsActive}
                    onChange={(e) => setFormIsActive(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingStrategy(null)
                    setEditError(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!user || !editingStrategy) return

                    if (!formName.trim()) {
                      setEditError('Strategy name is required')
                      return
                    }

                    try {
                      setUploading(true)
                      setEditError(null)

                      let imageUrl = editingStrategy.image_url

                      // Handle image upload/replacement (pro only)
                      if (formImage && isPro) {
                        // Delete old image if exists
                        if (editingStrategy.image_url) {
                          const urlParts = editingStrategy.image_url.split('/')
                          const fileName = urlParts[urlParts.length - 1]
                          const filePath = `${editingStrategy.user_id}/${fileName}`
                          await supabase.storage
                            .from('strategy-images')
                            .remove([filePath])
                        }

                        // Upload new image
                        const fileExt = formImage.name.split('.').pop()
                        const fileName = `${user.id}/${Date.now()}.${fileExt}`
                        const filePath = `strategy-images/${fileName}`

                        const { error: uploadError } = await supabase.storage
                          .from('strategy-images')
                          .upload(filePath, formImage, {
                            cacheControl: '3600',
                            upsert: false
                          })

                        if (uploadError) throw uploadError

                        const { data: urlData } = supabase.storage
                          .from('strategy-images')
                          .getPublicUrl(filePath)

                        imageUrl = urlData.publicUrl
                      }

                      // Update strategy
                      const { error: updateError } = await supabase
                        .from('strategies')
                        .update({
                          name: formName.trim(),
                          description: formDescription.trim() || null,
                          entry_rules: formEntryRules.trim() || null,
                          exit_rules: formExitRules.trim() || null,
                          image_url: imageUrl,
                          is_public: isPro ? formIsPublic : false,
                          is_active: formIsActive
                        })
                        .eq('id', editingStrategy.id)

                      if (updateError) throw updateError

                      // Refresh data
                      const decodedName = decodeURIComponent(strategyName || '')
                      const { data: strategiesData, error: strategiesError } = await supabase
                        .from('strategies')
                        .select('*')
                        .eq('user_id', user.id)
                        .eq('name', decodedName)
                        .order('version', { ascending: true })

                      if (strategiesError) throw strategiesError

                      setStrategies(strategiesData || [])
                      setShowEditModal(false)
                      setEditingStrategy(null)
                      
                      // Refresh version stats
                      const strategyIds = (strategiesData || []).map(s => s.id)
                      const { data: tradesData, error: tradesError } = await supabase
                        .from('trades_with_market_data')
                        .select('id, strategy_id, symbol, market, entry_date, exit_date, entry_price, exit_price, pnl, pnl_percentage, currency, long_short')
                        .in('strategy_id', strategyIds)
                        .order('entry_date', { ascending: false })

                      if (!tradesError && tradesData) {
                        const trades = tradesData as Trade[]
                        const statsWithTrades = await Promise.all(
                          (strategiesData || []).map(async (strategy) => {
                            const versionTrades = trades.filter(t => t.strategy_id === strategy.id)
                            return calculateVersionStats(strategy, versionTrades)
                          })
                        )
                        setVersionStats(statsWithTrades)
                        setOverallStats(calculateOverallStats(trades))
                      }
                    } catch (err: any) {
                      setEditError(err.message || 'Failed to update strategy')
                    } finally {
                      setUploading(false)
                    }
                  }}
                  disabled={uploading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Updating...' : 'Update Strategy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StrategyDetail

